import { Injectable } from '@angular/core';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { parseError } from '@new/common/utils/error-parser';
import { sortBySwissLocale } from '@new/common/utils/sort-by-swiss-locale';
import {
  assertIsDefined,
  isDefined
} from '@new/common/utils/type-guards/is-defined';
import { TranslateService } from '@ngx-translate/core';
import { Action, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ModalService } from '../../../../common/modal/modal.service';
import { ToastsService } from '../../../../common/services/toasts.service';
import { CreateLabelGQL } from '../../lib/services/labels/create-label.generated';
import { DeleteLabelGQL } from '../../lib/services/labels/delete-label.generated';
import { UpdateLabelGQL } from '../../lib/services/labels/update-label.generated';
import { LoadPoolDetailsGQL } from '../../lib/services/pool/load-pool.generated';
import { UnlockedExamsGQL } from '../../lib/services/tasks/unlocked-exams.generated';
import { ExamsState } from '../exams/exams.state';
import { QuestionGroupsState } from '../question-groups/question-groups.state';
import { TablePipelineState } from '../table-pipeline/table-pipeline.state';
import { TasksState } from '../tasks/tasks.state';
import * as Act from './pool.actions';
import { PoolStateModel, poolStateDefaults } from './pool.state.model';

@State<PoolStateModel>({
  name: 'pool',
  defaults: poolStateDefaults,
  children: [ExamsState, TasksState, QuestionGroupsState, TablePipelineState]
})
@Injectable()
export class PoolState {
  constructor(
    private readonly loadPoolDetailsService: LoadPoolDetailsGQL,
    private readonly createLabelService: CreateLabelGQL,
    private readonly updateLabelService: UpdateLabelGQL,
    private readonly deleteLabelService: DeleteLabelGQL,
    private readonly unlockedExamsService: UnlockedExamsGQL,
    private readonly modalService: ModalService,
    private readonly toasts: ToastsService,
    private readonly translate: TranslateService
  ) {}

  /* Query actions */

  @Action(Act.LoadPool, { cancelUncompleted: true })
  public loadPool(
    ctx: StateContext<PoolStateModel>,
    { id }: Act.LoadPool
  ): Observable<void> {
    ctx.patchState({
      pool: { ...ctx.getState().pool, query: { state: 'loading' } }
    });

    return this.loadPoolDetailsService.fetch({ poolId: id }).pipe(
      takeGraphQLResult(),
      switchMap(res => ctx.dispatch(new Act.LoadPoolSuccess(res.pool))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadPoolFailure(parseError(err), id))
      )
    );
  }

  @Action(Act.LoadPoolSuccess)
  public loadPoolSuccess(
    ctx: StateContext<PoolStateModel>,
    { pool }: Act.LoadPoolSuccess
  ): void {
    ctx.patchState({
      pool: {
        ...ctx.getState().pool,
        data: {
          ...pool,
          labels: [...pool.labels].sort((a, b) =>
            sortBySwissLocale(a.name, b.name)
          )
        },
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadPoolFailure)
  public loadPoolFailure(
    ctx: StateContext<PoolStateModel>,
    { error, id }: Act.LoadPoolFailure
  ): void {
    ctx.patchState({
      pool: { ...ctx.getState().pool, query: { state: 'failure', error } }
    });

    this.modalService.error({ errorCode: error.code }).then(retry => {
      if (!retry) return;
      ctx.dispatch(new Act.LoadPool(id));
    });
  }

  @Action(Act.LoadUnlockedExams)
  public loadUnlockedExams(
    ctx: StateContext<PoolStateModel>,
    { poolId }: Act.LoadUnlockedExams
  ): Observable<void> {
    ctx.patchState({
      unlockedExams: {
        ...ctx.getState().unlockedExams,
        query: { state: 'loading' }
      }
    });

    return this.unlockedExamsService.fetch({ poolId }).pipe(
      takeGraphQLResult(),
      switchMap(res =>
        ctx.dispatch(new Act.LoadUnlockedExamsSuccess(res.pool.exams))
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadUnlockedExamsFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadUnlockedExamsSuccess)
  public loadUnlockedExamsSuccess(
    ctx: StateContext<PoolStateModel>,
    { unlockedExams }: Act.LoadUnlockedExamsSuccess
  ): void {
    ctx.patchState({
      unlockedExams: {
        ...ctx.getState().unlockedExams,
        data: unlockedExams,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadUnlockedExamsFailure)
  public loadUnlockedExamsFailure(
    ctx: StateContext<PoolStateModel>,
    { error }: Act.LoadUnlockedExamsFailure
  ): void {
    ctx.patchState({
      unlockedExams: {
        ...ctx.getState().unlockedExams,
        query: { state: 'failure', error }
      }
    });
  }

  @Action(Act.PoolExamAdded)
  public updatePool(
    ctx: StateContext<PoolStateModel>,
    { exam }: Act.PoolExamAdded
  ): void {
    const pool = ctx.getState().pool.data;
    assertIsDefined(pool, 'Pool data is required to update the pool');

    ctx.patchState({
      pool: {
        ...ctx.getState().pool,
        data: {
          ...pool,
          exams: [...pool.exams, { id: exam.id }]
        }
      },
      unlockedExams: {
        ...ctx.getState().unlockedExams,
        data: [
          ...(ctx.getState().unlockedExams.data ?? []),
          ...(exam.locked ? [] : [{ id: exam.id, name: exam.name }])
        ]
      }
    });
  }

  @Action(Act.PoolExamDeleted)
  public poolExamDeleted(
    ctx: StateContext<PoolStateModel>,
    { examId }: Act.PoolExamDeleted
  ): void {
    const pool = ctx.getState().pool.data;
    assertIsDefined(pool, 'Pool data is required to update the pool');

    ctx.patchState({
      pool: {
        ...ctx.getState().pool,
        data: {
          ...pool,
          exams: pool.exams.filter(exam => exam.id !== examId)
        }
      },
      unlockedExams: {
        ...ctx.getState().unlockedExams,
        data: (ctx.getState().unlockedExams.data ?? []).filter(
          exam => exam.id !== examId
        )
      }
    });
  }

  /* Label mutations */

  @Action(Act.CreateLabel)
  public createLabel(
    ctx: StateContext<PoolStateModel>,
    { name, color }: Act.CreateLabel
  ): Observable<void> {
    const poolId = ctx.getState().pool.data?.id;
    assertIsDefined(poolId, 'Pool ID is required to create a label');

    ctx.patchState({
      pool: { ...ctx.getState().pool, mutation: { state: 'loading' } }
    });

    return this.createLabelService.mutate({ poolId, name, color }).pipe(
      takeGraphQLResult(),
      map(result => result?.createLabel?.label),
      filter(isDefined),
      switchMap(label => ctx.dispatch(new Act.CreateLabelSuccess(label))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.CreateLabelFailure(parseError(err)))
      )
    );
  }

  @Action(Act.CreateLabelSuccess)
  public createLabelSuccess(
    ctx: StateContext<PoolStateModel>,
    { label }: Act.CreateLabelSuccess
  ): void {
    const pool = ctx.getState().pool.data;
    assertIsDefined(pool, 'Pool data is required to update labels');

    ctx.patchState({
      pool: {
        ...ctx.getState().pool,
        data: {
          ...pool,
          labels: [...pool.labels, label].sort((a, b) =>
            sortBySwissLocale(a.name, b.name)
          )
        },
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.create', {
        resource: this.translate.instant('common.models.label')
      })
    );
  }

  @Action(Act.CreateLabelFailure)
  public createLabelFailure(
    ctx: StateContext<PoolStateModel>,
    { error }: Act.CreateLabelFailure
  ): void {
    ctx.patchState({
      pool: { ...ctx.getState().pool, mutation: { state: 'failure', error } }
    });

    this.toasts.addWarning(this.translate.instant('error_modal.title'));
  }

  @Action(Act.UpdateLabel)
  public updateLabel(
    ctx: StateContext<PoolStateModel>,
    { label }: Act.UpdateLabel
  ): Observable<void> {
    const poolId = ctx.getState().pool.data?.id;
    assertIsDefined(poolId, 'Pool ID is required to update a label');

    ctx.patchState({
      pool: { ...ctx.getState().pool, mutation: { state: 'loading' } }
    });

    return this.updateLabelService
      .mutate({
        poolId,
        labelId: label.id,
        name: label.name,
        color: label.color
      })
      .pipe(
        takeGraphQLResult(),
        switchMap(() => ctx.dispatch(new Act.UpdateLabelSuccess(label))),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.UpdateLabelFailure(parseError(err)))
        )
      );
  }

  @Action(Act.UpdateLabelSuccess)
  public updateLabelSuccess(
    ctx: StateContext<PoolStateModel>,
    { label }: Act.UpdateLabelSuccess
  ): void {
    const pool = ctx.getState().pool.data;
    assertIsDefined(pool, 'Pool data is required to update labels');

    ctx.patchState({
      pool: {
        ...ctx.getState().pool,
        data: {
          ...pool,
          labels: pool.labels.map(l => (l.id === label.id ? label : l))
        },
        mutation: { state: 'success' }
      }
    });
  }

  @Action(Act.UpdateLabelFailure)
  public updateLabelFailure(
    ctx: StateContext<PoolStateModel>,
    { error }: Act.UpdateLabelFailure
  ): void {
    ctx.patchState({
      pool: { ...ctx.getState().pool, mutation: { state: 'failure', error } }
    });

    this.toasts.addWarning(this.translate.instant('error_modal.title'));
  }

  @Action(Act.DeleteLabel)
  public deleteLabel(
    ctx: StateContext<PoolStateModel>,
    { labelId }: Act.DeleteLabel
  ): Observable<void> {
    const poolId = ctx.getState().pool.data?.id;
    assertIsDefined(poolId, 'Pool ID is required to delete a label');

    ctx.patchState({
      pool: { ...ctx.getState().pool, mutation: { state: 'loading' } }
    });

    return this.deleteLabelService.mutate({ poolId, labelId }).pipe(
      takeGraphQLResult(),
      switchMap(() => ctx.dispatch(new Act.DeleteLabelSuccess(labelId))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.DeleteLabelFailure(parseError(err)))
      )
    );
  }

  @Action(Act.DeleteLabelSuccess)
  public deleteLabelSuccess(
    ctx: StateContext<PoolStateModel>,
    { labelId }: Act.DeleteLabelSuccess
  ): void {
    const pool = ctx.getState().pool.data;
    assertIsDefined(pool, 'Pool data is required to update labels');

    ctx.patchState({
      pool: {
        ...ctx.getState().pool,
        data: {
          ...pool,
          labels: pool.labels.filter(label => label.id !== labelId)
        },
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.destroy', {
        resource: this.translate.instant('common.models.label')
      })
    );
  }

  @Action(Act.DeleteLabelFailure)
  public deleteLabelFailure(
    ctx: StateContext<PoolStateModel>,
    { error }: Act.DeleteLabelFailure
  ): void {
    ctx.patchState({
      pool: { ...ctx.getState().pool, mutation: { state: 'failure', error } }
    });

    this.toasts.addWarning(this.translate.instant('error_modal.title'));
  }

  @Action(Act.ReloadPool)
  public reloadPool(ctx: StateContext<PoolStateModel>): void {
    const currentPoolId = ctx.getState().pool.data?.id;

    assertIsDefined(currentPoolId);

    ctx.dispatch(new Act.LoadPool(currentPoolId));
  }
}
